import parse from 'html-react-parser';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import uniqBy from 'lodash/uniqBy';
import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import EventCard from 'src/2.0/cards/event-card';
import WidgetBreadcrumb from 'src/2.0/widgets/widget-breadcrumb';
import WidgetHeading from 'src/2.0/widgets/widget-heading';
import Layout from 'src/components/layouts';
import { APP_IMG, APP_URL, SHOW_COMMEMORATE, STICKY_AD_CATEGORIES, STICKY_AD_DURATION } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import { isRender, useDesktop } from 'src/utils/device';
import { rewriteTxt, timestamp } from 'src/utils/helper';
import { GET, GET_ARTICLES } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

const PageError = dynamic(import('pages/404'));
const DynamicAds = dynamic(import('src/utils/ads/dynamic-ads'));
const CardH = dynamic(import('src/components/cards/card-h'));
const CardV = dynamic(import('src/components/cards/card-v'));
const SectionFullH = dynamic(import('src/components/sections/section-full-h'));
const SectionAffiliate = dynamic(import('src/components/sections/section-event/section-affiliate'));
const StickyBox = dynamic(() => import('react-sticky-box'), { ssr: false });
const PageCategory = ({ articles, nextArticles, category, subCategory, lastNews, meta, ads, dataAffiliate, dataAdsHeader, sectionEvent }) => {
  const _data = useUser();
  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  if (articles === null) {
    return <PageError dataAdsHeader={dataAdsHeader} sessionSevId={sessionSevId} />;
  }
  const router = useRouter();
  /* -------------------------------- START : LOADMORE -------------------------------- */
  const nextLinkRef = useRef(nextArticles);
  const listArticlesRef = useRef(articles);
  const [listArticles, setListArticles] = useState(articles);
  const [show, setShow] = useState(12);
  const [adsBox, setAdsBox] = useState(true);
  const [isLoad, setIsLoad] = useState(true);
  const [loadData, setLoadData] = useState(false);

  const handleLightBoxClick = () => {
    setAdsBox(false);
  };

  const handleLoad = async () => {
    setLoadData(true);
    try {
      if (nextArticles && isLoad) {
        const resData = await GET_ARTICLES(`${nextLinkRef.current || ''}`, false, true);
        // console.log(`${nextLinkRef.current}${convertObjPath({ limit: 20 })}`)
        if (!isEmpty(resData?.data)) {
          nextLinkRef.current = resData?.nextTo || resData?.links?.next;
          const newLists = [...listArticlesRef.current, ...resData?.data];
          listArticlesRef.current = uniqBy(newLists, 'id');
          setListArticles(listArticlesRef.current);
        }
      }
      setShow(show + 12);
      setIsLoad(!isLoad);
    } catch (err) {
      console.error(`${timestamp()} ==========> LOAD_CATEGORY_LIST ERROR : ${nextLinkRef?.current}`, err);
    }
    setLoadData(false);
  };
  const cloneListArticles = [...listArticles];
  // console.log({ cloneListArticles, articles });
  const onTopArticle = cloneListArticles.splice(0, 5);

  let baseUrlCategory = `${APP_URL}/category/${category?.en}`;
  const _article = {
    title: meta?.title || WEB_TITLE,
    description: meta?.description || WEB_DESC,
    keyword: meta?.keyword || WEB_KEYWORD
  };

  let setItemListElement = '';

  if (subCategory?.en) {
    baseUrlCategory = `${APP_URL}/category/${category?.en}/${subCategory?.en}`;
    setItemListElement = `[
      {
        "@type": "ListItem",
        "position": 1,
        "name": "หน้าแรก",
        "item": "${APP_URL}"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "${category?.th}",
        "item": "${APP_URL}/category/${category?.en}"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "${subCategory?.th}",
        "item": "${baseUrlCategory}"
      }
    ]`;
  } else {
    setItemListElement = `[
      {
        "@type": "ListItem",
        "position": 1,
        "name": "หน้าแรก",
        "item": "${APP_URL}"
      },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "${category?.th}",
          "item": "${APP_URL}/category/${category?.en}"
        }
      ]`;
  }

  const { sub } = router.query;
  // const adsPremiumList = ['business'];
  // const adsFinanceList = ['business_finance'];
  // const adsEducationList = ['social_education'];
  // const isTabletOrMobile = useMediaQuery({ maxWidth: 768 });

  // console.log(nextLinkRef);
  // const titleEscape = escapeChar(meta.title);
  const categoryNameTH = subCategory?.th ? subCategory?.th : category?.th === 'ข่าวล่าสุด' ? 'Latest News' : category?.th;

  const isDesktop = useDesktop();
  const isStickyAdCategory = STICKY_AD_CATEGORIES?.includes(category?.en);

  const sentinelRef = useRef(null); // sentinel วางไว้เหนือ billboard เพื่อดักจับจังหวะ "เริ่ม stick"
  const stickyTimeoutRef = useRef(null);
  const hasStuckRef = useRef(false);
  const isStuckRef = useRef(false);
  const [stickyMenuHeight, setStickyMenuHeight] = useState(0); // ความสูงเมนู sticky เดิม
  const [isReleased, setIsReleased] = useState(false); // true = ปลด sticky หลังครบ 5 วิ
  const [isStuck, setIsStuck] = useState(false);

  const adsRef = useRef(null);
  const [adsHeight, setAdsHeight] = useState(null);
  useEffect(() => {
    isStuckRef.current = isStuck && !isReleased;
  }, [isStuck, isReleased]);

  useEffect(() => {
    const headerEl = document.querySelector('.header-desktop');
    const updateMenuHeight = () => setStickyMenuHeight(headerEl ? headerEl.offsetHeight : 0);
    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);
    return () => window.removeEventListener('resize', updateMenuHeight);
  }, []);

  useEffect(() => {
    if (!adsRef.current) return;

    const ro = new ResizeObserver(entries => {
      if (isStuckRef.current) return;

      const h = entries[0]?.contentRect?.height;
      if (h && h > 0) setAdsHeight(h);
    });
    ro.observe(adsRef.current);

    return () => ro.disconnect();
  }, [ads]);

  useEffect(() => {
    if (!isDesktop || !isStickyAdCategory || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          if (!hasStuckRef.current) {
            hasStuckRef.current = true;
            setIsStuck(true);
            setIsReleased(false);

            if (stickyTimeoutRef.current) clearTimeout(stickyTimeoutRef.current);
            stickyTimeoutRef.current = setTimeout(() => {
              setIsReleased(true);
              setIsStuck(false);
            }, STICKY_AD_DURATION);
          }
        } else {
          hasStuckRef.current = false;
          setIsStuck(false);
          setIsReleased(false);
          if (stickyTimeoutRef.current) {
            clearTimeout(stickyTimeoutRef.current);
            stickyTimeoutRef.current = null;
          }
        }
      },
      { rootMargin: `-${stickyMenuHeight}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
      if (stickyTimeoutRef.current) clearTimeout(stickyTimeoutRef.current);
    };
  }, [isDesktop, isStickyAdCategory, stickyMenuHeight]); // จะ re-run อีกครั้งพอ stickyMenuHeight อัปเดตจาก 0 เป็นค่าจริง

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        {...SEO}
        title={_article?.title}
        description={_article?.description}
        openGraph={{
          ...SEO.openGraph,
          title: _article?.title,
          description: _article?.description,
          url: baseUrlCategory
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword
          }
        ]}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={_article?.title} />
        <meta property='twitter:description' content={_article?.description} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                 __th_page="${subCategory?.en ? subCategory.en : category?.en}";
            `
          }}
        />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />
        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "${WEB_NAME}",
                "url": "${WEB_URL.WEB}",
                "description": "${_article?.description}",
                "sameAs": [
                  "${WEB_URL.FACEBOOK}",
                  "${WEB_URL.TWITTER}",
                  "${WEB_URL.INSTAGRAM}",
                  "${WEB_URL.YOUTUBE}",
                  "${WEB_URL.LINE}"
                ],
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "${WEB_URL.WEB}search?q={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              }
            `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                {
                  "@context": "http://schema.org",
                  "@type": "WebPage",
                  "name": "${categoryNameTH}",
                  "description": "${_article?.description}",
                  "publisher": {
                    "@type": "ProfilePage",
                    "name": "${WEB_NAME}"
                  }
                }
              `
          }}
        />

        {/* ------------------------ SPIPPET : BreadcrumbList ------------------------ */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "http://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": ${setItemListElement}
              }
            `
          }}
        />
      </Head>

      {!isEmpty(dataAffiliate) && <SectionAffiliate position={4} data={dataAffiliate} />}
      <Layout sessionSevId={sessionSevId}>
        {/* ---------------------------- ADS : LIGHTBOX ----------------------------- */}
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />}
        {/* ---------------------------- ADS : BILLBOARD ----------------------------- */}
        {/* {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />} */}
        {/* ---------------------------- ADS : BILLBOARD ----------------------------- */}
        <div ref={sentinelRef} />
        <BillboardWrapper $position={isDesktop && isStickyAdCategory && !isReleased} $pinned={isStuck && !isReleased} ref={adsRef} $fixedHeight={adsHeight} $topOffset={stickyMenuHeight}>
          {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}
        </BillboardWrapper>
        {/* {
          adsFinanceList.includes(sub) && <Ads
            adsClassName='ads'
            device='all'
            slot='/231599934,4696586/bangkokbiznews/Bitkub/billbord'
            position='div-gpt-ad-1641454399322-0'
            size={[[970, 250], [300, 250]]}
          />
        } */}
        {/* { adsEducationList.includes(sub) &&
          <div className='default-billboard'>
            {
              adsEducationList.includes(sub) && <Ads
                adsClassName='ads'
                device='all'
                slot='/231599934,4696586/bangkokbiznews/Social/billbord-Nida'
                position='div-gpt-ad-1643722284765-0'
                size={isTabletOrMobile ? [[336, 280], [300, 250]] : [[728, 90], [970, 250]]}
              />
            }
          </div>
        } */}
        <SectionWrapper id='section-1' className='category-wrapper'>
          <div className='container'>
            <WidgetBreadcrumb
              article={{
                section: category,
                subCategory: subCategory
              }}
            />
            {sub === 'upcoming' ? (
              <>
                <h1 className='heading-title' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  อีเวนต์ที่กำลังจะมาถึง
                </h1>
                <EventGrid style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  {!isEmpty(sectionEvent) &&
                    sectionEvent.map((item, index) => (
                      <EventCard
                        key={index}
                        item={item}
                        index={index}
                        onClick={(item, index) => {
                          sendDataLayerGlobal({
                            type: DATALAYER_TYPE.TRACK_POSITION,
                            router: router?.pathname,
                            section: 'อีเวนต์ที่กำลังจะมาถึง',
                            position: 'หน้า upcoming',
                            data: {
                              title: item?.title,
                              index: index + 1,
                              heading: 'อีเวนต์ที่กำลังจะมาถึง'
                            }
                          });
                        }}
                      />
                    ))}
                </EventGrid>
              </>
            ) : (
              <>
                <h1 className='heading-title' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                  {categoryNameTH}
                </h1>
                <div className='container'></div>
                <div className='row'>
                  <div className='col-lg-8 col-md-12'>
                    <div className='row' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                      {listArticles.length < 1 ? (
                        <div className='col-12 no-article'>
                          <img src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                        </div>
                      ) : (
                        <>
                          <div className='col-12'>
                            <SectionFullH
                              data={onTopArticle}
                              heading={false}
                              include={false}
                              txtSize={isRender('desktop') ? '--large' : '--small'}
                              ads={ads}
                              //! DATALAYER
                              eventDataLayer={{
                                type: DATALAYER_TYPE.TRACK_POSITION,
                                router: router?.pathname,
                                section: 'ข่าวทั้งหมด',
                                position: 'ใต้:โฆษณาตำแหน่ง billboard',
                                data: {
                                  block: '1',
                                  heading: categoryNameTH
                                }
                              }}
                            />
                            <div className='row'>
                              {cloneListArticles.slice(0, show).map((item, index) => (
                                <div key={item.id} id={index + 1} className='col-6 col-md-4 mb-3'>
                                  <CardV
                                    data={item}
                                    description={false}
                                    badge={true}
                                    path={item.link}
                                    //! DATALAYER
                                    eventDataLayer={{
                                      type: DATALAYER_TYPE.TRACK_POSITION,
                                      router: router?.pathname,
                                      section: 'ข่าวทั้งหมด',
                                      position: 'ใต้:โฆษณาตำแหน่ง billboard',
                                      data: {
                                        block: '1',
                                        title: item.title,
                                        heading: categoryNameTH,
                                        index: index + 6
                                      }
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                          <ButtonContainer>{loadData ? <text> Loading ... </text> : <button onClick={handleLoad}> ดูเพิ่มเติม</button>}</ButtonContainer>

                          {/* </InfiniteScroll> */}
                        </>
                      )}
                    </div>
                    <div id='postartice' className='ads-sticky-overflow'>
                      {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
                    </div>
                  </div>

                  <div className='col-lg-4 col-md-12 d-lg-block d-none'>
                    {StickyBox && (
                      <StickyBox offsetTop={70} offsetBottom={70}>
                        {/* ---------------------------- ADS : STICKY-1 ----------------------------- */}

                        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.STICKY_1} data={ads} />}

                        <div className='widget-content' style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
                          <WidgetHeading heading='Latest News' />

                          <div className='block-content'>
                            {Array.isArray(lastNews) && lastNews.length < 1 ? (
                              <div className='no-article'>
                                <img className='--side' src={`${APP_IMG}/no-article.png`} alt='no-article' width='100%' />
                              </div>
                            ) : (
                              <ul className='block-list'>
                                {lastNews.slice(0, 4).map((item, index) => (
                                  <li key={index}>
                                    <CardH
                                      data={item}
                                      large={false}
                                      //! DATALAYER
                                      eventDataLayer={{
                                        type: DATALAYER_TYPE.TRACK_POSITION,
                                        router: router?.pathname,
                                        section: 'Latest News',
                                        position: 'ใต้:โฆษณาตำแหน่ง Sticky Box',
                                        data: {
                                          block: '2',
                                          title: item.title,
                                          heading: 'Latest News',
                                          index: index + 1
                                        }
                                      }}
                                    />
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        {/* ---------------------------- ADS : STICKY-2 ----------------------------- */}
                        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.STICKY_2} data={ads} />}
                      </StickyBox>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </SectionWrapper>
        {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
      </Layout>
    </>
  );
};

export async function getServerSideProps({ req, res, query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: null,
    nextArticles: null,
    category: null,
    subCategory: null,
    lastNews: null,
    meta: null,
    ads: [],
    dataAffiliate: [],
    dataAdsHeader: null,
    sectionEvent: [],
    backgroundImage: ''
  };
  const scriptBrowser = await GET('/api/headerAds');
  if (!isEmpty(scriptBrowser)) {
    initProps.dataAdsHeader = scriptBrowser || '';
  }
  const url = req.url;
  let uri = `categories/${rewriteTxt(query?.cid)}`;
  if (query.sub) {
    if (rewriteTxt(query?.sub) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else if (rewriteTxt(query?.cid) === 'latest-news') {
      uri = `categories/${rewriteTxt(query?.cid)}`;
    } else {
      uri = `categories/${rewriteTxt(query?.cid)}/${query?.sub}`;
    }
  } else if (url.startsWith('/tag')) {
    uri = `tag/${encodeURIComponent(query.cid)}`;
  }

  if (query?.sub === 'upcoming') {
    const dataSetSectionEvent = await GET('/api/v1.0/categories/event/upcoming/advertorials?isActiveOnly=1', false, true);
    initProps.sectionEvent = dataSetSectionEvent?.data || [];
  } else {
    initProps.sectionEvent = [];
  }
  try {
    const regex = /([ก-๙])/;
    if (regex.test(query?.cid)) {
      res.setHeader('location', '/category/latest-news');
      res.statusCode = 301;
      res.end();
    }
    const resData = await GET_ARTICLES(`/api/v1.0/${uri}`, false, true);
    let resDataTo = null;

    if (resData?.statusCode === 404 || isUndefined(resData)) {
      return {
        notFound: true
      };
    }
    if (!isEmpty(resData)) {
      if (isEmpty(resData?.data)) {
        resDataTo = await GET_ARTICLES(`${resData?.nextTo}`, false, true);
        // console.log('#1', resDataTo);
        initProps.articles = resDataTo?.data || [];
      } else {
        initProps.articles = resData?.data || [];
      }
      initProps.nextArticles = resData?.nextTo || resData?.links?.next || null;
      initProps.category = resData?.category;
      initProps.subCategory = resData?.subCategory;
      initProps.lastNews = resData?.last_news || [];
      initProps.meta = resData.meta;
      initProps.ads = resData?.ads || [];
      initProps.dataAffiliate = resData?.affiliate || [];
      initProps.backgroundImage = resData?.category?.meta_data?.background || '';
    }
  } catch (err) {
    console.log('==========> CATEGORY ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }
  return {
    props: initProps
  };
}

export default PageCategory;

const ButtonContainer = styled.div`
  text-align: center;
  padding-bottom: 20px;
  button {
    width: 100%;
    border: 1px solid #e3e3e3;
    background-color: #e3e3e3;
    color: #555555;
    border-radius: 5px;
    font-size: 18px;
    padding: 5px 0;
    :hover {
      border: 1px solid #0600e7;
      background-color: #0600e7;
      color: #fff;
      transition: background-color 0.8ms linear;
    }
  }
`;
const SectionWrapper = styled.div`
  .empty-bg {
    background: unset !important;
  }
  .heading-title {
    font-size: 30pt;
    font-family: 'DBHelvethaicaXBd', sans-serif;
  }
  .block-content {
    .card-v-content-title {
      font-size: 24pt !important;
      font-family: 'DBHelvethaicaXBd', sans-serif !important;
    }
    @media (max-width: 690px) {
      .card-v-content-title {
        font-size: 20pt !important;
      }
      .card-h-content-title {
        font-size: 16pt !important;
      }
    }
  }
  /* .block-content {
    margin-top: 20px;
  } */
`;
const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 24px;
  margin: 25px 0;
  min-height: 500px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .event-card-thumb {
    border-radius: 0px;
  }
`;

const BillboardWrapper = styled.div`
  ${({ $position, $topOffset }) =>
    $position &&
    `
      position: sticky;
      top: ${$topOffset}px;
      z-index: 9;
    `}

  ${({ $fixedHeight }) =>
    $fixedHeight &&
    `
      .desktop .ads-billboard {
        min-height: ${$fixedHeight}px;
      }
    `}

  ${({ $pinned }) =>
    $pinned &&
    `
      .desktop .ads-billboard {
        background: none;
        min-height: unset;
      }
    `}
`;
